import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Building2, ArrowLeft, CheckCircle, Phone, AlertCircle, Mail, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { 
  getTierLimits, 
  TIER_NAMES, 
  TIER_PRICING, 
  formatCurrency,
  SubscriptionTier 
} from "@/lib/subscriptionFeatures";

// List of free email domains to block for free tier
const FREE_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
  'aol.com', 'icloud.com', 'mail.com', 'protonmail.com',
  'yandex.com', 'zoho.com', 'gmx.com', 'live.com'
];

// Tier feature descriptions for display
const TIER_FEATURES: Record<SubscriptionTier, string[]> = {
  free: [
    '1 lokasi kerja',
    '100 percakapan AI per bulan',
    'Laporan bahaya (teks saja)',
    'Check-in shift dasar',
    'Psikometrik pekerja',
  ],
  professional: [
    '3 lokasi kerja',
    '5.000 percakapan AI per bulan',
    'Laporan bahaya lengkap (foto & suara)',
    'Evidence Score & Status Tracking',
    'Analitik & Trend Analysis',
    'Dashboard Admin lengkap',
    '15 dokumen knowledge base',
  ],
  enterprise: [
    '6 lokasi kerja',
    '25.000 percakapan AI per bulan',
    'Semua fitur Professional +',
    'AI Safety Copilot',
    'Fatigue Management',
    'Worker & Cluster Diagnostics AI',
    'A/B Experiments',
    '50 dokumen knowledge base',
  ],
  enterprise_plus: [
    'Unlimited lokasi kerja',
    'Unlimited percakapan AI',
    'Semua fitur Enterprise +',
    'White Labeling',
    'API Access',
    'Dedicated CSM',
    'Custom Integration',
  ],
};

const CompanySignup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  
  // Read tier from URL parameter, default to 'free'
  const selectedTier = (searchParams.get('tier') || 'free') as SubscriptionTier;
  const tierLimits = getTierLimits(selectedTier);
  const tierName = TIER_NAMES[selectedTier] || 'Free';
  const tierPrice = TIER_PRICING[selectedTier]?.monthly || 0;
  
  const [formData, setFormData] = useState({
    // Company details
    company_name: '',
    industry_sector: 'manufacturing',
    company_size: '11-50',
    npwp: '', // Tax ID for fraud prevention
    // Admin details
    admin_name: '',
    admin_email: '',
    admin_phone: '',
    admin_password: '',
    admin_password_confirm: '',
  });

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  // Validate email domain - only block free domains for free tier
  const validateEmail = (email: string): boolean => {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return false;
    
    // Block free email domains only for free tier
    if (selectedTier === 'free' && FREE_EMAIL_DOMAINS.includes(domain)) {
      setEmailError('Untuk pendaftaran gratis, gunakan email perusahaan (bukan Gmail, Yahoo, dll). Atau hubungi sales untuk opsi berbayar.');
      return false;
    }
    
    setEmailError(null);
    return true;
  };

  const handleEmailChange = (email: string) => {
    setFormData({ ...formData, admin_email: email });
    if (email.includes('@')) {
      validateEmail(email);
    } else {
      setEmailError(null);
    }
  };

  // Format phone number to E.164
  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    let cleaned = phone.replace(/\D/g, '');
    
    // Handle Indonesian phone numbers
    if (cleaned.startsWith('0')) {
      cleaned = '62' + cleaned.substring(1);
    } else if (!cleaned.startsWith('62') && cleaned.length > 0) {
      cleaned = '62' + cleaned;
    }
    
    return '+' + cleaned;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.admin_password !== formData.admin_password_confirm) {
      toast({
        title: "Password Mismatch",
        description: "Passwords do not match. Please try again.",
        variant: "destructive",
      });
      return;
    }

    if (formData.admin_password.length < 6) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 6 characters.",
        variant: "destructive",
      });
      return;
    }

    // Validate email domain
    if (!validateEmail(formData.admin_email)) {
      toast({
        title: "Email Tidak Valid",
        description: emailError || "Gunakan email perusahaan untuk pendaftaran.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Check if NPWP already registered (if provided)
      if (formData.npwp) {
        const { data: existingNpwp } = await supabase
          .from('companies')
          .select('id')
          .eq('npwp', formData.npwp)
          .maybeSingle();

        if (existingNpwp) {
          toast({
            title: "NPWP Sudah Terdaftar",
            description: "NPWP ini sudah digunakan oleh perusahaan lain.",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
      }

      // 1. Sign up the admin user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.admin_email,
        password: formData.admin_password,
        options: {
          emailRedirectTo: `${window.location.origin}/admin`,
          data: {
            full_name: formData.admin_name,
          }
        }
      });

      if (authError) {
        // Handle "User already registered" error specifically
        if (authError.message.includes('User already registered') || authError.message.includes('already been registered')) {
          toast({
            title: "Email Sudah Terdaftar",
            description: "Email ini sudah terdaftar. Silakan login atau gunakan email lain.",
            variant: "destructive",
          });
          setIsLoading(false);
          return;
        }
        throw authError;
      }
      
      // Check for duplicate email when email confirmation is enabled
      // Supabase returns a user with empty identities instead of an error
      if (!authData.user || (authData.user.identities && authData.user.identities.length === 0)) {
        toast({
          title: "Email Sudah Terdaftar",
          description: "Email ini sudah terdaftar di sistem. Silakan login atau gunakan email lain.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      // 2. Create the company with all related records using SECURITY DEFINER function
      const slug = generateSlug(formData.company_name);

      const phoneRaw = formData.admin_phone?.trim() || "";
      const formattedPhone = phoneRaw ? formatPhoneNumber(phoneRaw) : null;

      const npwpRaw = formData.npwp?.trim() || "";
      const normalizedNpwp = npwpRaw ? npwpRaw : null;

      // RPC creates company as FREE tier and sets upgrade request atomically if paid tier selected
      const { data: companyId, error: rpcError } = await supabase.rpc('create_company_with_admin', {
        p_company_name: formData.company_name,
        p_slug: slug,
        p_subscription_tier: 'free',
        p_ai_pool_limit: 100,
        p_max_sites: 1,
        p_billing_email: formData.admin_email,
        p_phone: formattedPhone,
        p_npwp: normalizedNpwp,
        p_admin_user_id: authData.user.id,
        p_admin_name: formData.admin_name,
        p_industry_sector: formData.industry_sector,
        p_company_size: formData.company_size,
        p_requested_tier: selectedTier, // This sets upgrade_requested_tier atomically for paid tiers
      });

      if (rpcError) {
        console.error('RPC Error:', rpcError);
        throw rpcError;
      }

      console.log('Company created successfully with ID:', companyId);

      const isPaidTier = selectedTier !== 'free';
      toast({
        title: isPaidTier ? "Pendaftaran Berhasil!" : "Perusahaan Berhasil Dibuat!",
        description: isPaidTier 
          ? "Tim kami akan menghubungi Anda untuk aktivasi paket."
          : "Silakan cek email Anda untuk verifikasi akun.",
      });

      // Move to success step
      setStep(3);

    } catch (error: any) {
      console.error('Signup error:', error);
      
      // Map common backend errors to user-friendly Indonesian messages
      let errorMessage = "Gagal membuat perusahaan. Silakan coba lagi.";
      
      if (error.message?.includes('npwp_taken')) {
        errorMessage = "NPWP ini sudah digunakan perusahaan lain.";
      } else if (error.message?.includes('worker_profiles') || error.message?.includes('foreign key') || error.message?.includes('duplicate key')) {
        errorMessage = "Email ini sudah terdaftar. Silakan login atau gunakan email lain.";
      } else if (error.message?.includes('slug')) {
        errorMessage = "Nama perusahaan sudah digunakan. Silakan gunakan nama lain.";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast({
        title: "Pendaftaran Gagal",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="company_name">Nama Perusahaan *</Label>
        <Input
          id="company_name"
          placeholder="Nama Perusahaan Anda"
          value={formData.company_name}
          onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
          className="h-12 rounded-xl"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="industry">Industri *</Label>
        <Select
          value={formData.industry_sector}
          onValueChange={(value) => setFormData({ ...formData, industry_sector: value })}
        >
          <SelectTrigger className="h-12 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manufacturing">Manufaktur</SelectItem>
            <SelectItem value="construction">Konstruksi</SelectItem>
            <SelectItem value="oil_gas">Minyak & Gas</SelectItem>
            <SelectItem value="mining">Pertambangan</SelectItem>
            <SelectItem value="logistics">Logistik</SelectItem>
            <SelectItem value="healthcare">Kesehatan</SelectItem>
            <SelectItem value="other">Lainnya</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="company_size">Ukuran Perusahaan *</Label>
        <Select
          value={formData.company_size}
          onValueChange={(value) => setFormData({ ...formData, company_size: value })}
        >
          <SelectTrigger className="h-12 rounded-xl">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1-10">1-10 karyawan</SelectItem>
            <SelectItem value="11-50">11-50 karyawan</SelectItem>
            <SelectItem value="51-200">51-200 karyawan</SelectItem>
            <SelectItem value="201-500">201-500 karyawan</SelectItem>
            <SelectItem value="500+">500+ karyawan</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="npwp">NPWP (Opsional)</Label>
        <Input
          id="npwp"
          placeholder="00.000.000.0-000.000"
          value={formData.npwp}
          onChange={(e) => setFormData({ ...formData, npwp: e.target.value })}
          className="h-12 rounded-xl"
        />
        <p className="text-xs text-muted-foreground">
          NPWP membantu verifikasi perusahaan Anda
        </p>
      </div>

      <Button
        type="button"
        onClick={() => setStep(2)}
        disabled={!formData.company_name}
        className="w-full h-12 rounded-xl"
      >
        Lanjutkan
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="space-y-2">
        <Label htmlFor="admin_name">Nama Lengkap *</Label>
        <Input
          id="admin_name"
          placeholder="Nama Lengkap Anda"
          value={formData.admin_name}
          onChange={(e) => setFormData({ ...formData, admin_name: e.target.value })}
          className="h-12 rounded-xl"
          required
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="admin_email" className="flex items-center gap-2">
          <Mail className="h-4 w-4" />
          Email Perusahaan *
        </Label>
        <Input
          id="admin_email"
          type="email"
          placeholder="anda@perusahaan.com"
          value={formData.admin_email}
          onChange={(e) => handleEmailChange(e.target.value)}
          className={`h-12 rounded-xl ${emailError ? 'border-destructive' : ''}`}
          required
        />
        {emailError && (
          <div className="flex items-start gap-2 text-destructive text-xs">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{emailError}</span>
          </div>
        )}
        <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
          <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Email akan diverifikasi.</span> Pastikan email aktif karena Anda akan menerima link verifikasi untuk mengaktifkan akun.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="admin_phone">Nomor Telepon (Opsional)</Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="admin_phone"
            type="tel"
            placeholder="081234567890"
            value={formData.admin_phone}
            onChange={(e) => setFormData({ ...formData, admin_phone: e.target.value })}
            className="h-12 rounded-xl pl-10"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          Untuk keperluan kontak darurat (tidak wajib)
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="admin_password">Password *</Label>
        <Input
          id="admin_password"
          type="password"
          placeholder="Minimal 6 karakter"
          value={formData.admin_password}
          onChange={(e) => setFormData({ ...formData, admin_password: e.target.value })}
          className="h-12 rounded-xl"
          required
          minLength={6}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="admin_password_confirm">Konfirmasi Password *</Label>
        <Input
          id="admin_password_confirm"
          type="password"
          placeholder="Konfirmasi password Anda"
          value={formData.admin_password_confirm}
          onChange={(e) => setFormData({ ...formData, admin_password_confirm: e.target.value })}
          className="h-12 rounded-xl"
          required
        />
      </div>

      {/* Dynamic tier info based on URL parameter */}
      <div className={`p-4 rounded-xl text-sm ${
        selectedTier === 'free' ? 'bg-primary/5 border border-primary/20' :
        selectedTier === 'professional' ? 'bg-blue-500/10 border border-blue-500/30' :
        selectedTier === 'enterprise' ? 'bg-emerald-500/10 border border-emerald-500/30' :
        'bg-amber-500/10 border border-amber-500/30'
      }`}>
        <div className="flex items-center justify-between mb-2">
          <p className={`font-medium ${
            selectedTier === 'free' ? 'text-primary' :
            selectedTier === 'professional' ? 'text-blue-600' :
            selectedTier === 'enterprise' ? 'text-emerald-600' :
            'text-amber-600'
          }`}>
            Paket {tierName} {tierPrice > 0 ? `- ${formatCurrency(tierPrice)}/bulan` : ''}
          </p>
        </div>
        <ul className="space-y-1 text-muted-foreground text-xs">
          {TIER_FEATURES[selectedTier]?.map((feature, idx) => (
            <li key={idx}>• {feature}</li>
          ))}
        </ul>
      </div>

      <div className="flex gap-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => setStep(1)}
          className="flex-1 h-12 rounded-xl"
        >
          Kembali
        </Button>
        <Button
          type="submit"
          disabled={isLoading || !formData.admin_email || !formData.admin_password || !!emailError}
          className="flex-1 h-12 rounded-xl"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Memproses...
            </>
          ) : (
            selectedTier === 'free' ? 'Mulai Gratis' : `Daftar ${tierName}`
          )}
        </Button>
      </div>
    </form>
  );

  const renderStep3 = () => {
    const isPaidTier = selectedTier !== 'free';
    
    return (
      <div className="text-center space-y-6">
        <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center">
          <CheckCircle className="h-8 w-8 text-emerald-500" />
        </div>
        <div>
          <h3 className="text-xl font-semibold mb-2">
            {isPaidTier ? 'Pendaftaran Berhasil!' : 'Perusahaan Berhasil Dibuat!'}
          </h3>
          <p className="text-muted-foreground">
            {isPaidTier ? (
              <>Pendaftaran untuk <strong>{formData.company_name}</strong> dengan paket <strong>{tierName}</strong> telah diterima.</>
            ) : (
              <>Perusahaan <strong>{formData.company_name}</strong> telah didaftarkan dengan paket <strong>{tierName}</strong>.</>
            )}
          </p>
        </div>

        {/* Pending approval notice for paid tiers */}
        {isPaidTier && (
          <div className="bg-amber-500/10 rounded-xl p-5 border border-amber-500/30">
            <div className="flex items-center justify-center gap-2 mb-3">
              <AlertCircle className="h-6 w-6 text-amber-600" />
              <h4 className="font-semibold text-amber-700">Menunggu Persetujuan</h4>
            </div>
            <p className="text-sm text-muted-foreground">
              Tim kami akan menghubungi Anda untuk aktivasi paket <strong>{tierName}</strong>. 
              Anda dapat login sekarang dengan fitur dasar (Free). 
              Fitur berbayar akan diaktifkan setelah persetujuan.
            </p>
          </div>
        )}
        
        {/* Email verification emphasis */}
        <div className="bg-primary/10 rounded-xl p-5 border border-primary/30">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Mail className="h-6 w-6 text-primary" />
            <h4 className="font-semibold text-primary">Verifikasi Email Diperlukan</h4>
          </div>
          <p className="text-sm text-muted-foreground mb-2">
            Kami telah mengirim email verifikasi ke:
          </p>
          <p className="font-medium text-foreground bg-background/80 rounded-lg py-2 px-4 inline-block">
            {formData.admin_email}
          </p>
          <p className="text-xs text-muted-foreground mt-3">
            Klik link di email untuk mengaktifkan akun Anda sebelum login.
          </p>
        </div>

        <div className="p-4 rounded-xl bg-muted/50 text-sm text-left">
          <p className="font-medium text-foreground mb-2">Langkah selanjutnya:</p>
          <ol className="space-y-1 text-muted-foreground text-xs list-decimal list-inside">
            <li><strong>Cek email</strong> dan klik link verifikasi</li>
            <li>Login ke dashboard admin</li>
            {isPaidTier && <li>Tunggu aktivasi paket {tierName} dari tim kami</li>}
            <li>Tambahkan pekerja ke sistem</li>
            <li>Mulai laporkan bahaya!</li>
          </ol>
        </div>
        <Button onClick={() => navigate('/admin/login')} className="w-full h-12 rounded-xl">
          Ke Halaman Login Admin
        </Button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/30 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {step > 1 ? 'Kembali' : 'Back'}
        </Button>

        <div className="bg-card border rounded-3xl p-6 shadow-lg">
          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {step === 3 ? 'Selesai!' : 'Daftarkan Perusahaan'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {step === 1 && 'Langkah 1: Informasi Perusahaan'}
                {step === 2 && 'Langkah 2: Akun Administrator'}
                {step === 3 && 'Pendaftaran berhasil'}
              </p>
            </div>
          </div>

          {/* Step indicator */}
          {step < 3 && (
            <div className="flex gap-2 mb-6">
              <div className={`flex-1 h-1.5 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-muted'}`} />
              <div className={`flex-1 h-1.5 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-muted'}`} />
            </div>
          )}

          {/* Form content */}
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>
      </motion.div>
    </div>
  );
};

export default CompanySignup;
